import { api } from './api.js';
import { photoTakenOn } from './exif-date.js';
import type { UploadTarget } from './types.js';

export const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
export const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** The files the server will take, and how many were left out (wrong type or over 10 MB). */
export function usablePhotos(files: File[]): { usable: File[]; skipped: number } {
  const usable = files.filter((f) => PHOTO_TYPES.includes(f.type) && f.size <= MAX_PHOTO_BYTES);
  return { usable, skipped: files.length - usable.length };
}

/**
 * Sends photos straight to object storage with the signed form the API hands out, then tells the
 * API each one arrived. The API never touches the image bytes. `onSent` runs after each photo so
 * a screen can show progress.
 */
export async function uploadPhotos(
  propertyId: string,
  files: File[],
  onSent: (sent: number, total: number) => void = () => undefined,
): Promise<void> {
  // The day the file says it was taken; with none, the API writes the upload day.
  const days = await Promise.all(files.map((f) => photoTakenOn(f)));
  const targets = await api<UploadTarget[]>(`/properties/${propertyId}/photos/uploads`, {
    method: 'POST',
    body: {
      files: files.map((f, i) => ({
        contentType: f.type,
        ...(days[i] == null ? {} : { takenOn: days[i] }),
      })),
    },
  });
  for (const [index, target] of targets.entries()) {
    const form = new FormData();
    for (const [name, value] of Object.entries(target.upload.fields)) form.append(name, value);
    form.append('file', files[index] as File);
    const res = await fetch(target.upload.url, { method: 'POST', body: form, credentials: 'omit' });
    if (!res.ok) throw new Error(`storage answered ${String(res.status)}`);
    await api(`/properties/${propertyId}/photos/${target.photoId}/confirm`, { method: 'POST' });
    onSent(index + 1, targets.length);
  }
}
